/**
 * PromptComposer — the platform's single prompt "class".
 *
 * A modern one-line composer (grows on type) with the Run/Send action AND an
 * optional agent picker tucked INSIDE the pill — the same chrome the Network
 * Workbench and Agentic rooms use (built on ``WorkbenchPromptCard`` +
 * ``useWorkbenchPromptFieldHeight``), so adopting it anywhere keeps the product
 * visually unified. Zero new runtime deps.
 *
 * It is intentionally a CONTROLLED, presentational component: the parent owns
 * ``value`` and the agent selection. Submit fires on Enter (Shift+Enter = newline)
 * or the Run button. Keep heavyweight chat concerns (streaming, approval lease,
 * attachments) in the caller — this is just the input surface.
 *
 * Usage:
 *   <PromptComposer
 *     value={prompt} onChange={setPrompt}
 *     onRun={run} running={running}
 *     agents={[{ agentId, agentLabel }]} agentId={agentId} onAgentChange={setAgentId}
 *     placeholder="Ask about this room's context…"
 *     runLabel="Run"
 *   />
 */
import React, { useRef } from 'react';
import { ChevronDown, Play } from 'lucide-react';
import WorkbenchPromptCard from './WorkbenchPromptCard.jsx';
import { useWorkbenchPromptFieldHeight } from '../hooks/useWorkbenchPromptFieldHeight.js';
import {
  getWorkbenchPromptInputClassName,
  WORKBENCH_PROMPT_FIELD_STYLE,
} from '../utils/workbenchPromptFieldClassNames.js';

/**
 * @param {object} props
 * @param {string} props.value
 * @param {(next: string) => void} props.onChange
 * @param {() => void} props.onRun                 — fired on Enter / Run click when enabled.
 * @param {boolean} [props.running]                — shows the spinner + disables Run.
 * @param {boolean} [props.disabled]               — hard-disable the whole composer.
 * @param {boolean} [props.allowEmpty]             — let Run fire with an empty prompt (default false).
 * @param {string}  [props.placeholder]
 * @param {string}  [props.runLabel]               — Run button text (default "Run").
 * @param {Array<{agentId: string, agentLabel?: string}>} [props.agents] — when given, an agent
 *        dropdown renders inside the pill (bottom-left).
 * @param {string}  [props.agentId]
 * @param {(agentId: string) => void} [props.onAgentChange]
 * @param {string}  [props.allAgentsLabel]         — option for "no specific agent" (default shown).
 * @param {React.ReactNode} [props.bottomLeftExtra] — extra controls beside the agent picker.
 * @param {React.ReactNode} [props.hint]            — small centered hint under the card.
 * @param {React.ReactNode} [props.alertBand]       — rear notice card (Claude-style stack).
 * @param {number}  [props.maxHeightPx]
 * @param {string}  [props.testId]
 */
export default function PromptComposer({
  value,
  onChange,
  onRun,
  running = false,
  disabled = false,
  allowEmpty = false,
  placeholder = 'Type a prompt…',
  runLabel = 'Run',
  agents = null,
  agentId = '',
  onAgentChange,
  allAgentsLabel = 'All agents',
  bottomLeftExtra = null,
  hint = null,
  alertBand = null,
  maxHeightPx = 200,
  // Compact one-liner: input + controls + send on a single row (default). Set
  // false to fall back to the taller two-deck card.
  singleRow = true,
  testId = 'prompt-composer',
}) {
  const fieldRef = useRef(null);
  const promptHeight = useWorkbenchPromptFieldHeight(fieldRef, value, { maxHeightPx });
  // Single-row pill until the prompt grows past ~3 lines, then the two-deck card.
  const effectiveSingleRow = singleRow && promptHeight <= 64;

  const canRun = !disabled && !running && (allowEmpty || String(value || '').trim().length > 0);

  const fireRun = () => {
    if (!canRun) return;
    onRun?.();
  };

  const hasAgentPicker = Array.isArray(agents);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        fireRun();
      }}
      className="relative z-20"
      data-testid={testId}
    >
      <WorkbenchPromptCard
        singleRow={effectiveSingleRow}
        alertBand={alertBand}
        top={(
          <textarea
            ref={fieldRef}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                fireRun();
              }
            }}
            disabled={disabled}
            rows={1}
            placeholder={placeholder}
            aria-multiline="true"
            className={getWorkbenchPromptInputClassName({ disabled })}
            style={WORKBENCH_PROMPT_FIELD_STYLE}
            data-testid={`${testId}-input`}
          />
        )}
        bottomLeft={(
          <div className="flex items-center gap-2">
            {hasAgentPicker ? (
              <div className="relative">
                <select
                  value={agentId}
                  onChange={(e) => onAgentChange?.(e.target.value)}
                  disabled={disabled}
                  aria-label="Agent"
                  data-testid={`${testId}-agent`}
                  className="
                    appearance-none rounded-full border border-gray-200 bg-gray-50
                    py-1 pl-3 pr-7 text-xs font-medium text-gray-700
                    outline-none transition-colors hover:border-gray-300
                    focus:border-blue-400 focus:ring-1 focus:ring-blue-200
                    disabled:cursor-not-allowed disabled:opacity-50
                  "
                >
                  <option value="">{allAgentsLabel}</option>
                  {agents.map((a) => (
                    <option key={a.agentId} value={a.agentId}>
                      {a.agentLabel || a.agentId}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                  aria-hidden
                />
              </div>
            ) : null}
            {bottomLeftExtra}
          </div>
        )}
        bottomRight={<RunButton canRun={canRun} running={running} label={runLabel} testId={testId} />}
        hint={hint}
      />
    </form>
  );
}

function RunButton({ canRun, running, label, testId }) {
  return (
    <button
      type="submit"
      disabled={!canRun}
      aria-label={label}
      data-testid={`${testId}-run`}
      className={`
        inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all
        ${
          !canRun
            ? 'cursor-not-allowed bg-gray-200 text-gray-400'
            : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:shadow-md hover:scale-[1.03] active:scale-95'
        }
      `}
    >
      {running ? (
        <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden>
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        <Play className="h-3.5 w-3.5" aria-hidden />
      )}
      <span>{running ? 'Running…' : label}</span>
    </button>
  );
}
